import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { DailyPortfolioStock } from '../services/api';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import { localStorageUtils } from '../utils/localStorage';
import { convertToYahooTicker, getStockPriceHistory } from '../utils/stockDataService';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';
import { DocumentIcon, CloseIcon } from './Icons';
import './DailyPortfolioModal.css';

// 종목코드로 통화 판단
const getCurrencyFromStockCode = (stockCode?: string) => {
  if (!stockCode) return 'KRW';
  // 숫자로만 구성되어 있으면 한국 주식 (KRW)
  if (/^\d+$/.test(stockCode)) {
    return 'KRW';
  }
  // 그 외는 미국 주식 (USD)
  return 'USD';
};

// 통화에 따라 금액 포맷팅
const formatCurrency = (amount: number, currency: string) => {
  if (currency === 'USD') {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
  } else {
    return `${amount.toLocaleString('ko-KR')}원`;
  }
};

interface DailyPortfolioModalProps {
  date: Date;
  onClose: () => void;
}

type SortField = 'stockName' | 'stockCode' | 'holdingQuantity' | 'averagePrice' | 'currentPrice' | 'profitRateChange' | 'todayProfitRate' | 'profitAmount';
type SortDirection = 'asc' | 'desc' | null;

const DailyPortfolioModal: React.FC<DailyPortfolioModalProps> = ({ date, onClose }) => {
  const { modalRef } = useModalAccessibility(true, onClose);
  const { isAuthenticated } = useAuth();
  const [stocks, setStocks] = useState<DailyPortfolioStock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const navigate = useNavigate();

  // 로컬스토리지 데이터로 보유 종목 계산
  const calculateLocalPortfolio = async (targetDate: Date): Promise<DailyPortfolioStock[]> => {
    const localInvestments = localStorageUtils.getInvestments();
    const dateStr = format(targetDate, 'yyyy-MM-dd');
    const targetDateObj = new Date(dateStr);
    
    // 해당 날짜까지의 투자 기록 필터링
    const recordsUpToDate = localInvestments.filter(inv => {
      if (!inv.date && !inv.recordDate) return false;
      const invDate = new Date(inv.date || inv.recordDate);
      return invDate <= targetDateObj && !inv.isDeleted;
    });
    
    // 종목별 보유 수량 및 평균 단가 계산
    const stockMap = new Map<string, {
      stockName: string;
      stockCode: string;
      totalQuantity: number;
      totalAmount: number;
      buyRecords: any[];
      ticker?: string;
    }>();
    
    recordsUpToDate.forEach(inv => {
      if (!inv.stockCode || !inv.stockName) return;
      
      const key = inv.stockCode;
      
      if (!stockMap.has(key)) {
        stockMap.set(key, {
          stockName: inv.stockName,
          stockCode: inv.stockCode,
          totalQuantity: 0,
          totalAmount: 0,
          buyRecords: [],
          ticker: inv.ticker || convertToYahooTicker(key)
        });
      }
      
      const stock = stockMap.get(key)!;
      
      if (inv.type === 'BUY') {
        const quantity = inv.quantity || 0;
        const pricePerShare = inv.pricePerShare || 0;
        const totalAmount = inv.totalAmount || (pricePerShare * quantity);
        
        stock.totalQuantity += quantity;
        stock.totalAmount += totalAmount;
        stock.buyRecords.push(inv);
      } else if (inv.type === 'SELL') {
        const sellQuantity = inv.sellQuantity || inv.quantity || 0;
        const sellPricePerShare = inv.pricePerShare || 0;
        const sellTotalAmount = inv.totalAmount || (sellPricePerShare * sellQuantity);
        
        // 매도 시 FIFO 방식으로 평균 단가 조정
        // 간단히 수량만 차감하고, 평균 단가는 유지
        stock.totalQuantity -= sellQuantity;
        if (stock.totalQuantity < 0) stock.totalQuantity = 0;
        
        // 매도된 수량만큼 총액에서 차감 (평균 단가 기준)
        if (stock.totalQuantity > 0 && stock.buyRecords.length > 0) {
          const avgPrice = stock.totalAmount / (stock.totalQuantity + sellQuantity);
          stock.totalAmount = avgPrice * stock.totalQuantity;
        } else {
          stock.totalAmount = 0;
        }
      }
    });
    
        // 해당 날짜의 실제 종가 조회 (오늘이면 현재가, 과거면 히스토리)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        targetDateObj.setHours(0, 0, 0, 0);
        
        // 히스토리 조회 기간: 대상 날짜 -10일 ~ 대상 날짜 (전일 종가 확보 위해 여분 포함)
        const histStartDate = new Date(targetDateObj);
        histStartDate.setDate(histStartDate.getDate() - 10);
        const histStart = format(histStartDate, 'yyyy-MM-dd');
        // 오늘 이후는 오늘까지만
        const histEnd = format(targetDateObj <= today ? targetDateObj : today, 'yyyy-MM-dd');
        
        // 종목별로 히스토리 조회 (병렬)
        // Map<stockCode, Map<date, closePrice>>
        const priceHistoryCache = new Map<string, Record<string, number>>();
        const tickerMap = new Map<string, string>(); // stockCode → ticker
        
        stockMap.forEach((stock, stockCode) => {
          tickerMap.set(stockCode, stock.ticker || convertToYahooTicker(stockCode));
        });
        
        await Promise.all(
          Array.from(tickerMap.entries()).map(async ([stockCode, ticker]) => {
            const history = await getStockPriceHistory(ticker, histStart, histEnd);
            if (Object.keys(history).length > 0) {
              priceHistoryCache.set(stockCode, history);
            }
          })
        );
        
        // 대상 날짜의 종가 및 전일 종가 찾기 헬퍼
        const findClosestPrice = (history: Record<string, number>, targetDateStr: string): { close: number | null; prevClose: number | null } => {
          const sortedDates = Object.keys(history).sort();
          // 대상 날짜 이전 마지막 거래일 찾기
          const datesOnOrBefore = sortedDates.filter(d => d <= targetDateStr);
          if (datesOnOrBefore.length === 0) return { close: null, prevClose: null };
          const closeDateStr = datesOnOrBefore[datesOnOrBefore.length - 1];
          const close = history[closeDateStr];
          const prevIdx = datesOnOrBefore.length - 2;
          const prevClose = prevIdx >= 0 ? history[datesOnOrBefore[prevIdx]] : null;
          return { close, prevClose };
        };
        
        // DailyPortfolioStock 형식으로 변환
        const portfolioStocks: DailyPortfolioStock[] = [];
        
        stockMap.forEach((stock, stockCode) => {
          if (stock.totalQuantity > 0) {
            const averagePrice = stock.totalQuantity > 0 ? stock.totalAmount / stock.totalQuantity : 0;
            const currency = getCurrencyFromStockCode(stockCode);
            
            const history = priceHistoryCache.get(stockCode);
            const { close: currentPrice, prevClose: prevClosePrice } = history
              ? findClosestPrice(history, dateStr)
              : { close: null, prevClose: null };
            
            const price = currentPrice || 0;
            const profitAmount = price > 0 ? (price - averagePrice) * stock.totalQuantity : 0;
            const todayProfitRate = averagePrice > 0 && price > 0
              ? ((price - averagePrice) / averagePrice) * 100
              : 0;
            // 전날 대비 변동률 = (해당일 종가 - 전일 종가) / 평균단가 * 100
            const profitRateChange = averagePrice > 0 && price > 0 && prevClosePrice != null && prevClosePrice > 0
              ? ((price - prevClosePrice) / averagePrice) * 100
              : 0;
            
            portfolioStocks.push({
              stockName: stock.stockName,
              stockCode: stockCode,
              holdingQuantity: stock.totalQuantity,
              averagePrice: averagePrice,
              currentPrice: price,
              previousProfitRate: 0,
              profitRateChange: profitRateChange,
              todayProfitRate: todayProfitRate,
              profitAmount: profitAmount,
              currency: currency,
              exchangeRate: currency === 'USD' ? 1.0 : undefined
            });
          }
        });
        
        return portfolioStocks;
  };

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const dateStr = format(date, 'yyyy-MM-dd');
        
        if (isAuthenticated) {
          // 로그인한 경우: API에서 조회
          const response = await apiService.getDailyPortfolio(dateStr);
          
          if (response.success && response.data) {
            setStocks(response.data.stocks || []);
          } else {
            setError(getUnifiedApiErrorMessage(response));
          }
        } else {
          // 로그인하지 않은 경우: 로컬스토리지에서 계산
          const localStocks = await calculateLocalPortfolio(date);
          setStocks(localStocks);
        }
      } catch (err: any) {
        console.error('보유 종목 조회 오류:', err);
        const serverMsg = getApiFailureMessage(err, '');
        // 401 에러나 인증 관련 에러는 로컬스토리지 데이터로 대체
        if (err.response?.status === 401 ||
            serverMsg.includes('인증') ||
            serverMsg.includes('로그인')) {
          // 로그인하지 않은 경우 로컬스토리지 데이터 사용
          if (!isAuthenticated) {
            const localStocks = await calculateLocalPortfolio(date);
            setStocks(localStocks);
            setError(null);
          } else {
            setError(null);
            setStocks([]);
          }
        } else {
          setError(getApiFailureMessage(err, '보유 종목 정보를 불러오는 중 오류가 발생했습니다.'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPortfolio();
  }, [date, isAuthenticated]);

  const handleWriteJournal = () => {
    navigate('/investments', { state: { selectedDate: date } });
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatPercent = (num: number): string => {
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  // 정렬 처리
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // 같은 필드 클릭 시: 오름차순 -> 내림차순 -> 정렬 없음
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // 다른 필드 클릭 시: 오름차순으로 시작
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 환율을 적용해서 원화로 변환하는 헬퍼 함수
  const convertToKRW = (amount: number, currency: string, exchangeRate?: number): number => {
    if (currency === 'KRW' || !currency) {
      return amount;
    }
    // USD 등 외화인 경우 환율 적용
    const rate = exchangeRate || 1.0;
    return amount * rate;
  };

  // 정렬된 종목 목록
  const sortedStocks = React.useMemo(() => {
    if (!sortField || !sortDirection) {
      return stocks;
    }

    return [...stocks].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'stockName':
          aValue = a.stockName || '';
          bValue = b.stockName || '';
          break;
        case 'stockCode':
          aValue = a.stockCode || '';
          bValue = b.stockCode || '';
          break;
        case 'holdingQuantity':
          aValue = a.holdingQuantity || 0;
          bValue = b.holdingQuantity || 0;
          break;
        case 'averagePrice':
          // 평균단가: 환율 적용해서 원화로 변환 후 비교
          aValue = convertToKRW(a.averagePrice || 0, a.currency || 'KRW', a.exchangeRate);
          bValue = convertToKRW(b.averagePrice || 0, b.currency || 'KRW', b.exchangeRate);
          break;
        case 'currentPrice':
          // 현재가: 환율 적용해서 원화로 변환 후 비교
          aValue = convertToKRW(a.currentPrice || 0, a.currency || 'KRW', a.exchangeRate);
          bValue = convertToKRW(b.currentPrice || 0, b.currency || 'KRW', b.exchangeRate);
          break;
        case 'profitRateChange':
          aValue = a.profitRateChange || 0;
          bValue = b.profitRateChange || 0;
          break;
        case 'todayProfitRate':
          aValue = a.todayProfitRate || 0;
          bValue = b.todayProfitRate || 0;
          break;
        case 'profitAmount':
          // 수익금: 이미 원화로 계산되어 있지만, 환율이 있으면 확인
          aValue = convertToKRW(a.profitAmount || 0, a.currency || 'KRW', a.exchangeRate);
          bValue = convertToKRW(b.profitAmount || 0, b.currency || 'KRW', b.exchangeRate);
          break;
        default:
          return 0;
      }

      // 문자열 비교
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue, 'ko-KR')
          : bValue.localeCompare(aValue, 'ko-KR');
      }

      // 숫자 비교
      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [stocks, sortField, sortDirection]);

  // 정렬 아이콘 렌더링
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <span className="sort-icon">⇅</span>;
    }
    if (sortDirection === 'asc') {
      return <span className="sort-icon">↑</span>;
    }
    if (sortDirection === 'desc') {
      return <span className="sort-icon">↓</span>;
    }
    return <span className="sort-icon">⇅</span>;
  };

  return (
    <div className="daily-portfolio-modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="daily-portfolio-modal modal-animated"
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-portfolio-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="daily-portfolio-title">보유 종목 현황</h2>
          <span className="date">{format(date, 'yyyy년 MM월 dd일')}</span>
          <button className="close-btn" onClick={onClose}><CloseIcon size={20} color="currentColor" /></button>
        </div>

        {isLoading && (
          <div className="loading">로딩 중...</div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        {!isLoading && !error && (
          <>
            {stocks.length === 0 ? (
              <div className="empty-message">보유 종목이 없습니다.</div>
            ) : (
              <div className="portfolio-table-container">
                <table className="portfolio-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('stockName')}>
                        종목명 {renderSortIcon('stockName')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('stockCode')}>
                        종목코드 {renderSortIcon('stockCode')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('holdingQuantity')}>
                        보유수량 {renderSortIcon('holdingQuantity')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('averagePrice')}>
                        평균단가 {renderSortIcon('averagePrice')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('currentPrice')}>
                        현재가 {renderSortIcon('currentPrice')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('profitRateChange')}>
                        전날대비 {renderSortIcon('profitRateChange')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('todayProfitRate')}>
                        수익률 {renderSortIcon('todayProfitRate')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('profitAmount')}>
                        수익금 {renderSortIcon('profitAmount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStocks.map((stock, index) => {
                      const currency = stock.currency || getCurrencyFromStockCode(stock.stockCode);
                      const currentPrice = stock.currentPrice || 0;
                      return (
                        <tr key={index}>
                          <td className="stock-name-cell">{stock.stockName}</td>
                          <td className="stock-code-cell">{stock.stockCode}</td>
                          <td>{formatNumber(stock.holdingQuantity)}주</td>
                          <td>{formatCurrency(stock.averagePrice, currency)}</td>
                          <td>{currentPrice > 0 ? formatCurrency(currentPrice, currency) : '-'}</td>
                          <td className={stock.profitRateChange !== 0 ? (stock.profitRateChange >= 0 ? 'positive' : 'negative') : ''}>
                            {stock.profitRateChange !== 0 ? formatPercent(stock.profitRateChange) : '-'}
                          </td>
                          <td className={stock.todayProfitRate >= 0 ? 'positive' : 'negative'}>
                            {formatPercent(stock.todayProfitRate)}
                          </td>
                          <td className={stock.profitAmount >= 0 ? 'positive' : 'negative'}>
                            {formatCurrency(stock.profitAmount, currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div className="modal-footer">
          <button className="write-journal-btn" onClick={handleWriteJournal}>
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DocumentIcon size={16} color="#fff" /></span> 일지 작성
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyPortfolioModal;

